import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useMembership } from "@/hooks/use-membership";
import { useListAllRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import type { RunnerPlaceholder } from "@/queries/runner-manager-queries";
import { useListAvailableRunnerManagers } from "@/queries/runner-manager-queries";
import { useListRunners, type UseListRunnersParams, type PlainRunner } from "@/queries/runner-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { RunnerKind, RunnerProvider, type SCMIntegration } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { memo, useMemo, useState, type FC } from "react";
import { AddNewRunnerCard, PlaceholderCard, RunnerCard } from "./RunnerCard";
import { Button } from "@/components/flexkit/Button";
import { withGitpodSize } from "@/assets/icons/utils";
import { Heading3 } from "@/components/podkit/typography/Headings.tsx";
import { cn } from "@/components/podkit/lib/cn";
import { Input } from "@/components/podkit/forms/Input";
import IconPlusSmall from "central-icons/IconPlusSmall";
import { Search } from "lucide-react";
import { NewRunnerModal } from "@/components/runners/NewRunnerModal";

export type RunnersListProps = {
    filter: UseListRunnersParams;
    excludeProvider?: RunnerProvider;
    emptyStateMessage?: string;
    placeholders?: RunnerPlaceholder[];
    title?: string;
};

const PlaceholderCards = memo<{ placeholders: RunnerPlaceholder[] }>(({ placeholders }) => (
    <>
        {placeholders.map((placeholder) => (
            <PlaceholderCard key={placeholder.runnerManagerId} placeholder={placeholder} />
        ))}
    </>
));
PlaceholderCards.displayName = "PlaceholderCards";

type RunnerCardItemProps = {
    runner: PlainRunner;
    integrations: SCMIntegration[];
    isPhasedOut: boolean;
};

const RunnerCardItem = memo<RunnerCardItemProps>(({ runner, integrations, isPhasedOut }) => (
    <RunnerCard runner={runner} integrations={integrations} isPhasedOut={isPhasedOut} />
));
RunnerCardItem.displayName = "RunnerCardItem";

export const RunnersList: FC<RunnersListProps> = ({
    filter,
    excludeProvider,
    emptyStateMessage,
    placeholders = [],
    title,
}) => {
    const { data: user, isPending: isPendingUser } = useAuthenticatedUser();

    const useListRunnersFilter = { ...filter, creatorId: filter.kind === RunnerKind.LOCAL ? user?.id : undefined };
    const { data: runnersData, isPending: isPendingRunners } = useListRunners(useListRunnersFilter);

    // Get available RunnerManager IDs for phased-out detection
    const { availableRunnerManagers, isPending: isPendingRunnerManagers } = useListAvailableRunnerManagers();
    const availableManagerIds = useMemo(
        () => new Set(availableRunnerManagers.map((m) => m.runnerManagerId)),
        [availableRunnerManagers],
    );
    const [showNewRunnerModal, setShowNewRunnerModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // Filter out excluded providers client-side
    const filteredRunners = useMemo(() => {
        const runners = runnersData || [];
        if (!runners || !excludeProvider) return runners;
        return runners.filter((runner) => runner.provider !== excludeProvider);
    }, [runnersData, excludeProvider]);

    // Filter runners by search query
    const searchFilteredRunners = useMemo(() => {
        if (!searchQuery.trim()) return filteredRunners;
        const query = searchQuery.toLowerCase();
        return filteredRunners.filter((runner) => runner.name.toLowerCase().includes(query));
    }, [filteredRunners, searchQuery]);

    const membership = useMembership();

    const { data: integrations, isPending: integrationsPending } = useListAllRunnerSCMIntegrations(
        filteredRunners.map((runner) => runner.runnerId),
    );

    const integrationsByRunnerId = useMemo(() => {
        const integrationsByRunnerId = new Map<string, SCMIntegration[]>();
        for (const integration of integrations || []) {
            const runnerId = integration.runnerId;
            const runnerIntegrations = integrationsByRunnerId.get(runnerId) || [];
            runnerIntegrations.push(integration);
            integrationsByRunnerId.set(runnerId, runnerIntegrations);
        }
        return integrationsByRunnerId;
    }, [integrations]);

    const isPending = isPendingUser || isPendingRunners || integrationsPending || isPendingRunnerManagers;

    const showNewRunnerAction =
        filter.kind === RunnerKind.REMOTE && membership.membership?.userRole === OrganizationRole.ADMIN;

    if (isPending) {
        return <LoadingState />;
    }

    if (filteredRunners.length === 0 && placeholders.length === 0 && !showNewRunnerAction) {
        return (
            <EmptyStateCard
                title={emptyStateMessage ? "No Runners" : "No Runners Available"}
                description={
                    emptyStateMessage ||
                    "There are currently no runners set up for this organization. Please contact your organization admin to set up runners."
                }
                data-testid="no-runners-available"
            />
        );
    }

    const showSearch = filteredRunners.length > 1;

    return (
        <>
            <div className="flex justify-between">
                <Heading3>{title}</Heading3>
                <Button
                    size="sm"
                    LeadingIcon={withGitpodSize(IconPlusSmall)}
                    onClick={() => setShowNewRunnerModal(true)}
                    className={cn("hidden", { "inline-flex": showNewRunnerAction && filteredRunners.length > 0 })}
                    data-tracking-id="new-runner-runners-list"
                >
                    New Runner
                </Button>
            </div>
            {showSearch && (
                <div className="flex items-center justify-between gap-4">
                    <div className="relative max-w-md flex-1">
                        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-tertiary" />
                        <Input
                            placeholder="Search runners by name"
                            className="pl-10"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            data-testid="runner-search-input"
                        />
                    </div>
                </div>
            )}
            <div data-testid="runners-list">
                {searchFilteredRunners.length === 0 && searchQuery.trim() ? (
                    <EmptyStateCard
                        title="No matching runners"
                        description="No runners match your search. Try a different search term."
                        data-testid="no-matching-runners-empty-state"
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                        {searchFilteredRunners.map((runner) => {
                            const isPhasedOut =
                                runner.provider === RunnerProvider.MANAGED &&
                                !!runner.runnerManagerId &&
                                !availableManagerIds.has(runner.runnerManagerId);

                            return (
                                <RunnerCardItem
                                    key={runner.runnerId}
                                    runner={runner}
                                    integrations={integrationsByRunnerId.get(runner.runnerId) || []}
                                    isPhasedOut={isPhasedOut}
                                />
                            );
                        })}
                        <PlaceholderCards placeholders={placeholders} />
                        {showNewRunnerAction && filteredRunners.length === 0 && (
                            <AddNewRunnerCard setShowNewRunnerModal={setShowNewRunnerModal} />
                        )}
                    </div>
                )}
            </div>

            <NewRunnerModal open={showNewRunnerModal} onClose={() => setShowNewRunnerModal(false)} />
        </>
    );
};
